import { from, Observable, Observer, of, throwError } from 'rxjs';
import { filter, mergeMap, take } from 'rxjs/operators';
import { Logging } from '../diagnostics/logging';
import { Rpc } from './rpc';
import { RpcInboundClient } from './rpc-inbound-client';
import { RpcObservableRequest, RpcObservableRequestContext } from './rpc-observable-request';
import { RpcObservableResult, RpcObservableResultContext } from './rpc-observable-result';
import { RpcOutbound } from './rpc-outbound';
import { RpcOutboundClient } from './rpc-outbound-client';

/**
 * RPC Observable Client class.
 */
export class RpcObservableClient<
    TRequest extends RpcObservableResultContext,
    TResult extends RpcObservableRequestContext,
    TError extends RpcObservableRequestContext> {

    private id = 0;
    private requestCommand: string;
    private resultCommand: string;
    private resultBuffer: RpcObservableResult<TResult, TError>[] = [];
    private resultObservers: Observer<RpcObservableResult<TResult, TError>>[] = [];

    /**
     * Initializes a new instance of the RpcObservableClient class.
     *
     * @param rpc The rpc object.
     * @param command the command string.
     * @param version the version string.
     */
    constructor(protected rpc: Rpc, protected command: string, protected version: string) {
        this.requestCommand = `${command}-Request`;
        this.resultCommand = `${command}-Result`;
        this.rpc.register(this.resultCommand, this.handler.bind(this));
        this.rpc.registerInboundHandler(this.resultCommand, this.handler.bind(this));
        this.rpc.registerOutboundHandler(this.resultCommand, this.handler.bind(this));
    }

    /**
     * Make observable call.
     *
     * @param request The the request data.
     * @param outbound The outbound RPC channel. (request initiated from Shell.)
     * @return An observable for the response message
     */
    protected observableCall(request: TRequest, outbound?: RpcOutbound): Observable<TResult> {
        const id = this.id++;
        const observableRequest: RpcObservableRequest<TRequest> = { id, request };
        Logging.logDebug(this.requestCommand, `Sending request: "${this.requestCommand}"`, observableRequest);
        const replayResult = this.replayResult();

        return from(this.rpcCall(observableRequest, outbound))
            .pipe(
                mergeMap(() => replayResult),
                filter(observableResult => {
                    if (observableResult.id !== observableRequest.id) {
                        return false;
                    }

                    // clean up the buffer.
                    const index = this.resultBuffer.findIndex(item => item.id === observableResult.id);
                    if (index >= 0) {
                        this.resultBuffer.splice(index, 1);
                    }

                    return true;
                }),
                take(1),
                mergeMap((result) => {
                    if (result.error) {
                        result.error.sourceName = result.sourceName;
                        result.error.sourceSubName = result.sourceSubName;
                        result.error.sourceVersion = result.sourceVersion;
                        return throwError(() => result.error);
                    }

                    result.result = result.result || <TResult>{};
                    result.result.sourceName = result.sourceName;
                    result.result.sourceSubName = result.sourceSubName;
                    result.result.sourceVersion = result.sourceVersion;
                    return of(result.result);
                }));
    }

    /**
     * Making rpc call to shell or module.
     *
     * @param request the request packet data.
     * @param outbound the outbound object from Shell to module request.
     * @return Promise<void> the promise object.
     */
    private rpcCall(request: RpcObservableRequest<TRequest>, outbound?: RpcOutbound): Promise<void> {
        if (outbound) {
            return RpcOutboundClient.callOutbound(this.rpc.rpcManager.rpcChannel, outbound, this.requestCommand, this.version, request);
        }

        return RpcInboundClient.call(this.rpc, this.requestCommand,  this.version, request);
    }

    /**
     * Create a replay observable. Replays result data if results exists.
     */
    private replayResult(): Observable<RpcObservableResult<TResult, TError>> {
        return new Observable(observer => {
            this.resultObservers.push(observer);

            // replay.
            for (const item of this.resultBuffer) {
                observer.next(item);
            }

            return () => {
                const index = this.resultObservers.findIndex(item => item === observer);
                if (index >= 0) {
                    this.resultObservers.splice(index, 1);
                }

                observer.complete();
            };
        });
    }

    /**
     * Handles rpc response messages.
     *
     * @param result the result of observable request.
     */
    private handler(result: RpcObservableResult<TResult, TError>): Promise<any> {
        // store.
        this.resultBuffer.push(result);

        // distributed to all active observers.
        for (const observer of this.resultObservers) {
            observer.next(result);
        }

        return Promise.resolve();
    }
}
